import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Plus,
  Copy,
  Check,
  ExternalLink,
  Smartphone,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      fetchEvents();
      fetchFeedUrl();
    }
  }, [user, currentDate]);

  const fetchEvents = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('meybra_token');
      
      // Get start and end of current month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await fetch(
        `${API_URL}/api/calendar/events?user_id=${user.id}&start_date=${startOfMonth.toISOString()}&end_date=${endOfMonth.toISOString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedUrl = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('meybra_token');
      const response = await fetch(
        `${API_URL}/api/calendar/feed/generate?user_id=${user.id}&user_role=${user.role}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFeedUrl(data.feed_url);
      }
    } catch (error) {
      console.error('Error fetching feed URL:', error);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const openCalendarApp = (type) => {
    let url = '';
    const webcalUrl = feedUrl.replace('https://', 'webcal://');
    
    switch (type) {
      case 'apple':
        url = webcalUrl;
        break;
      case 'google':
        url = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl)}`;
        break;
      case 'outlook':
        url = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl)}&name=MEYBRA%20Training`;
        break;
      default:
        url = feedUrl;
    }
    
    window.open(url, '_blank');
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getEventsForDate = (day) => {
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
    return events.filter(event => {
      const eventDate = new Date(event.start_time).toDateString();
      return eventDate === dateStr;
    });
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getEventTypeColor = (type) => {
    const colors = {
      'training_session': 'bg-primary/20 text-primary border-primary/30',
      'home_training': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'club_practice': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'game': 'bg-red-500/20 text-red-400 border-red-500/30',
      'tournament': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'video_review': 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  const getEventTypeDot = (type) => {
    const colors = {
      'training_session': 'bg-primary',
      'home_training': 'bg-emerald-500',
      'club_practice': 'bg-blue-500',
      'game': 'bg-red-500',
      'tournament': 'bg-purple-500',
      'video_review': 'bg-amber-500'
    };
    return colors[type] || 'bg-muted-foreground';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatEventType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Event';
  };

  // Get upcoming events (next 7 days)
  const upcomingEvents = events
    .filter(event => {
      const eventDate = new Date(event.start_time);
      const now = new Date();
      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      return eventDate >= now && eventDate <= weekFromNow;
    })
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .slice(0, 5);

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground">Calendar</h1>
            <p className="text-muted-foreground text-sm">View and sync your training schedule</p>
          </div>
          <Button 
            onClick={() => setShowSyncModal(true)}
            className="bg-primary text-black hover:bg-primary/90"
          >
            <Smartphone className="mr-2 h-4 w-4" />
            Sync Calendar
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={prevMonth}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <h2 className="text-lg font-semibold">{formatMonthYear(currentDate)}</h2>
              <button 
                onClick={nextMonth}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs text-muted-foreground font-medium py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {emptyDays.map(i => (
                <div key={`empty-${i}`} className="aspect-square p-1" />
              ))}
              
              {/* Days of the month */}
              {days.map(day => {
                const dayEvents = getEventsForDate(day);
                const hasEvents = dayEvents.length > 0;
                
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      aspect-square p-1 rounded-lg transition-colors relative
                      ${isToday(day) ? 'bg-primary/20 ring-1 ring-primary' : 'hover:bg-muted'}
                      ${selectedDate === day ? 'bg-muted' : ''}
                    `}
                  >
                    <span className={`text-sm ${isToday(day) ? 'text-primary font-bold' : ''}`}>
                      {day}
                    </span>
                    {hasEvents && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                        {dayEvents.slice(0, 3).map((event, idx) => (
                          <div 
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full ${getEventTypeDot(event.event_type)}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Date Events */}
            {selectedDate && (
              <div className="mt-4 pt-4 border-t border-border">
                <h3 className="text-sm font-medium mb-3">
                  Events on {new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <div className="space-y-2">
                  {getEventsForDate(selectedDate).length > 0 ? (
                    getEventsForDate(selectedDate).map(event => (
                      <div 
                        key={event.id}
                        className={`p-3 rounded-lg border ${getEventTypeColor(event.event_type)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm">{event.title}</p>
                            <p className="text-xs opacity-70">{formatEventType(event.event_type)}</p>
                          </div>
                          <span className="text-xs">{formatTime(event.start_time)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
                            <MapPin size={12} />
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No events scheduled</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Upcoming Events Sidebar */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Upcoming</h3>
              <button 
                onClick={fetchEvents}
                className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents.map(event => (
                  <div 
                    key={event.id}
                    className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${getEventTypeDot(event.event_type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock size={12} />
                          <span>
                            {new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span>{formatTime(event.start_time)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <MapPin size={12} />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="mx-auto text-muted-foreground mb-3" size={32} />
                <p className="text-sm text-muted-foreground">No upcoming events</p>
              </div>
            )}

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-3">Event Types</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span>Training</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>Home Training</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span>Club Practice</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>Game</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading">Sync with Phone Calendar</h2>
              <button 
                onClick={() => setShowSyncModal(false)}
                className="p-1 hover:bg-muted rounded"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Subscribe to your MEYBRA calendar to see all training sessions, practices, and games in your phone's calendar app.
            </p>

            {/* Calendar App Buttons */}
            <div className="space-y-3 mb-6">
              <button
                onClick={() => openCalendarApp('apple')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                  📅
                </div>
                <div className="text-left">
                  <p className="font-medium">Apple Calendar</p>
                  <p className="text-xs text-muted-foreground">iPhone, iPad, Mac</p>
                </div>
                <ExternalLink size={16} className="ml-auto text-muted-foreground" />
              </button>

              <button
                onClick={() => openCalendarApp('google')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-2xl">
                  📆
                </div>
                <div className="text-left">
                  <p className="font-medium">Google Calendar</p>
                  <p className="text-xs text-muted-foreground">Android, Web</p>
                </div>
                <ExternalLink size={16} className="ml-auto text-muted-foreground" />
              </button>

              <button
                onClick={() => openCalendarApp('outlook')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">
                  O
                </div>
                <div className="text-left">
                  <p className="font-medium">Outlook</p>
                  <p className="text-xs text-muted-foreground">Microsoft 365</p>
                </div>
                <ExternalLink size={16} className="ml-auto text-muted-foreground" />
              </button>
            </div>

            {/* Copy URL */}
            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground mb-2">Or copy calendar URL manually:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={feedUrl}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs bg-muted rounded-lg border border-border truncate"
                />
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              Your calendar will automatically update when sessions change.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
